import type { CategoryPack } from "./catalog";
import type { ProductResult, RecommendationStatus, ScoredProduct } from "./types";

const TOKEN_RE = /[a-z0-9]+/g;
const STOP_WORDS = new Set(["for", "and", "with", "the", "set", "pack", "amazon", "best"]);

export function scoreCollectedProduct(
  product: ProductResult,
  pack: CategoryPack,
  seedQuery: string,
  runId: string,
  collectedAt: string
): ScoredProduct {
  const reasons: string[] = [];
  const warnings: string[] = [];
  const title = product.title.toLowerCase();
  const titleTokens = tokens(product.title);
  const queryTokens = tokens(seedQuery);
  const overlap = [...queryTokens].filter((token) => titleTokens.has(token)).length;
  const titleMatchScore = queryTokens.size ? Math.round((overlap / queryTokens.size) * 24) : 0;
  if (titleMatchScore >= 14) {
    reasons.push("title-query-match");
  }

  const completenessScore =
    (product.asin ? 8 : 0) +
    (product.affiliateUrl ? 8 : 0) +
    (product.imageUrl ? 6 : 0) +
    (product.price !== null ? 4 : 0) +
    (product.rating !== null ? 4 : 0);

  const ratingScore = product.rating === null ? 8 : clamp(Math.round((product.rating - 3.6) * 12), 0, 16);
  if (product.rating !== null && product.rating >= pack.minRating) {
    reasons.push("rating-above-pack-threshold");
  }

  const reviewScore =
    product.reviewCount === null ? 8 : clamp(Math.round(Math.log10(product.reviewCount + 1) * 5), 0, 18);
  if (product.reviewCount !== null && product.reviewCount >= pack.minReviews) {
    reasons.push("review-volume-above-pack-threshold");
  }

  const priceScore = priceFitScore(product.price, pack.minPrice, pack.maxPrice);
  if (product.price !== null && product.price >= pack.minPrice && product.price <= pack.maxPrice) {
    reasons.push("price-in-editorial-range");
  }

  const sourceScore = product.source === "amazon-html" ? 8 : 4;
  const baseScore = Math.round(pack.topicScore * 0.2);
  let productRecommendScore = clamp(
    baseScore + titleMatchScore + completenessScore + ratingScore + reviewScore + priceScore + sourceScore,
    0,
    100
  );

  const excludedTerm = pack.excludeTerms.find((term) => title.includes(term.toLowerCase()));
  if (excludedTerm) {
    productRecommendScore = Math.min(productRecommendScore, 49);
    warnings.push(`excluded-term:${excludedTerm}`);
  }
  if (title.includes("sponsored ad")) {
    productRecommendScore = Math.min(productRecommendScore - 18, 67);
    warnings.push("sponsored-ad-result");
  }
  if (!product.asin || !product.affiliateUrl || !product.title) {
    productRecommendScore = Math.min(productRecommendScore, 49);
    warnings.push("missing-core-product-data");
  }

  const recommendationStatus = statusForScore(productRecommendScore, warnings);
  if (recommendationStatus === "auto_selected") {
    reasons.push("auto-selected-high-score");
  }

  return {
    ...product,
    runId,
    collectedAt,
    categoryPack: pack.key,
    seedQuery,
    contentTopicScore: pack.topicScore,
    productRecommendScore,
    recommendationStatus,
    recommendationReasons: reasons,
    warnings
  };
}

export function mergeScoredProducts(products: ScoredProduct[]): ScoredProduct[] {
  const byAsin = new Map<string, ScoredProduct>();
  for (const product of products) {
    const existing = byAsin.get(product.asin);
    if (!existing || product.productRecommendScore > existing.productRecommendScore) {
      byAsin.set(product.asin, product);
    }
  }
  return [...byAsin.values()].sort((a, b) => {
    if (a.recommendationStatus !== b.recommendationStatus) {
      return statusRank(a.recommendationStatus) - statusRank(b.recommendationStatus);
    }
    return b.productRecommendScore - a.productRecommendScore;
  });
}

function statusForScore(score: number, warnings: string[]): RecommendationStatus {
  if (warnings.includes("missing-core-product-data")) {
    return "excluded";
  }
  if (score >= 78) {
    return "auto_selected";
  }
  if (score >= 60) {
    return "candidate";
  }
  return "excluded";
}

function statusRank(status: RecommendationStatus): number {
  if (status === "auto_selected") {
    return 0;
  }
  if (status === "candidate") {
    return 1;
  }
  return 2;
}

function priceFitScore(price: number | null, min: number, max: number): number {
  if (price === null) {
    return 8;
  }
  if (price >= min && price <= max) {
    return 14;
  }
  if (price < min) {
    return 6;
  }
  return price <= max * 1.35 ? 8 : 3;
}

function tokens(value: string): Set<string> {
  return new Set((value.toLowerCase().match(TOKEN_RE) || []).filter((token) => token.length > 1 && !STOP_WORDS.has(token)));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
